import CSS from 'csstype';
import Carregando from '@/shared/components/Carregando';
import { DataAmigavel, DiasEntreDuasDatas } from '@/shared/utils/datas';
import { Fragment, useState } from 'react';
import { IFullReport, IReportLocalLotacao, IReportLotacao, IReportPessoaLotada, IReportAtuacao } from '@/api.types';
import { TipoLotacao } from '../enums/tipo-lotacao.enum';
import { TipoPeriodoDescansoList } from '@/shared/constants/enumlist';
import { TrimAll } from '@/shared/utils/textos';

const stylesPadding: CSS.Properties = {
  padding: '2vh',
};

interface IMyProps {
  agrupar: boolean;
  datasValidas: boolean;
  exibirEventos: boolean;
  exibirEstagiarios: boolean;
  relatorio: IFullReport | null;
  selectedDefensores: string[];
  selectedDefensorias: string[];
  selectedComarcas: string[];
  agruparPorPredio: boolean;
}

export default function RelatorioDefensoresContent({
  datasValidas: agruparAreaAtuacao,
  exibirEventos,
  exibirEstagiarios,
  relatorio,
  selectedDefensores,
  selectedDefensorias,
  selectedComarcas,
  agruparPorPredio,
}: IMyProps) {

  const [dadosResponsaveis] = useState<any[]>([]);
  const [filtroDefensoria] = useState(false);

  let titulo1 = 0;

  function AgruparEstagiariosPorResponsavel(defensoria: IReportLocalLotacao) {
    if (!relatorio)
      return [];

    // Obter todas as lotações de estagiários nesta defensoria
    const lotacoesEstagiarios = relatorio.lotacoes.filter((a) => a.defensoria_id == defensoria.id && a.estagio_id);

    if (lotacoesEstagiarios.length === 0) {
      return [<Fragment key={`vago-${defensoria.id}`}>(VAGO)</Fragment>];
    }

    // Agrupar estagiários por responsável
    const estagiariosPorResponsavel: { [key: string]: any[] } = {};

    lotacoesEstagiarios.forEach((lotacao) => {
      const estagiario = relatorio.pessoas.find((a) =>
        a.estagios?.some((b) => b.id == lotacao.estagio_id),
      );

      if (estagiario) {
        // Encontrar o responsável deste estagiário
        const responsavelEstagiario = dadosResponsaveis?.find(
          (item) => item.estagiario?.id === estagiario.id
        )?.responsavel;

        const responsavelNome = responsavelEstagiario?.nome || '';

        if (!estagiariosPorResponsavel[responsavelNome]) {
          estagiariosPorResponsavel[responsavelNome] = [];
        }

        estagiariosPorResponsavel[responsavelNome].push({
          estagiario,
          lotacao,
          responsavel: responsavelEstagiario
        });
      }
    });

    // Renderizar os grupos
    const elementos: JSX.Element[] = [];

    Object.entries(estagiariosPorResponsavel).forEach(([responsavelNome, estagiarios], groupIndex) => {
      // Adicionar cabeçalho do responsável
      elementos.push(
        <Fragment key={`responsavel-${groupIndex}`}>
          <div className="responsavel-header">
            <strong title='Supervisor de Estágio.'>Supervisor(a): {responsavelNome}</strong>
          </div>
        </Fragment>
      );

      // Adicionar estagiários deste responsável
      estagiarios.forEach((item, index) => {
        elementos.push(
          <Fragment key={`estagiario-${item.lotacao.id}-${index}`}>
            <span className={!item.lotacao.ativo ? 'relatorio-defensor-inativo' : ''}>
              {item.estagiario.titulo} {item.estagiario.nome}
            </span>
            <br />
            <span className="fst-italic relatorio-defensor-observacao">
              ({DataAmigavel(item.lotacao.data_inicial)} - {DataAmigavel(item.lotacao.data_final)})
            </span>
            <br />
            <span className="fst-italic relatorio-defensor-observacao">
              {item.lotacao.anotacao1} {item.lotacao.anotacao2}
            </span>
            <br />
            {index < estagiarios.length - 1 && <hr className="m-1" />}
          </Fragment>
        );
      });

      // Adicionar separador entre grupos (exceto no último)
      if (groupIndex < Object.keys(estagiariosPorResponsavel).length - 1) {
        elementos.push(<hr key={`separator-${groupIndex}`} className="m-2" />);
      }
    });

    return elementos;
  }

  function GetOrdemDefensoresPorResponsaveis(defensoria: IReportLocalLotacao): string[] {
    if (!relatorio)
      return [];

    // Obter a lista de responsáveis dos estagiários nesta defensoria
    const responsaveisEstagiarios: string[] = [];

    const lotacoesEstagiarios = relatorio.lotacoes.filter(
      (a) => a.defensoria_id == defensoria.id && a.estagio_id,
    );

    lotacoesEstagiarios.forEach((lotacao) => {
      const estagiario = relatorio.pessoas.find((a) =>
        a.estagios?.some((b) => b.id == lotacao.estagio_id),
      );

      if (estagiario) {
        const responsavelEstagiario = dadosResponsaveis?.find(
          (item) => item.estagiario?.id === estagiario.id
        )?.responsavel;

        if (responsavelEstagiario && !responsaveisEstagiarios.includes(responsavelEstagiario.nome)) {
          responsaveisEstagiarios.push(responsavelEstagiario.nome);
        }
      }
    });

    return responsaveisEstagiarios;
  }

  function GetNomesDefensores(defensoria: IReportLocalLotacao) {
    if (!relatorio) return [];

    const lotacoes = relatorio.lotacoes.filter(
      (a) => a.defensoria_id == defensoria.id && a.defensor_id,
    );

    if (lotacoes.length === 0) {
      return [<Fragment key={`vago-${defensoria.id}`}>(VAGO)</Fragment>];
    }

    // Obter a ordem dos responsáveis dos estagiários
    const ordemResponsaveis = GetOrdemDefensoresPorResponsaveis(defensoria);

    // Agrupar defensores por tipo e ordenar pela ordem dos responsáveis
    const defensoresTitulares = lotacoes.filter(a => a.tipo === TipoLotacao.Titularidade);
    const outrosDefensores = lotacoes.filter(a => a.tipo !== TipoLotacao.Titularidade);

    // Ordenar defensores pela ordem dos responsáveis
    const ordenarDefensores = (defensores: IReportLotacao[]) => {
      return defensores.sort((a, b) => {
        const defensorA = relatorio!.pessoas.find(p => p.id === a.defensor_id);
        const defensorB = relatorio!.pessoas.find(p => p.id === b.defensor_id);

        const indexA = ordemResponsaveis.findIndex(resp => resp.includes(defensorA?.nome || ''));
        const indexB = ordemResponsaveis.findIndex(resp => resp.includes(defensorB?.nome || ''));

        // Se ambos estão na lista de responsáveis, ordenar pela ordem da lista
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }

        // Se apenas um está na lista, priorizar o que está
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;

        // Se nenhum está na lista, manter ordem original
        return 0;
      });
    };

    const elementos: JSX.Element[] = [];

    if (defensoresTitulares.length > 0) {
      const titularesOrdenados = ordenarDefensores(defensoresTitulares);
      elementos.push(...RenderizarDefensoresDoGrupo(titularesOrdenados));
    } else {
      elementos.push(<Fragment key={'v' + defensoria.id}>(VAGO)<br /><br /></Fragment>)
    }

    if (outrosDefensores.length > 0) {
      if (defensoresTitulares.length > 0) {
        elementos.push(<hr key="separador-titulares-outros" className="m-2" />);
      }

      const outrosOrdenados = ordenarDefensores(outrosDefensores);
      elementos.push(...RenderizarDefensoresDoGrupo(outrosOrdenados));
    }

    return elementos;
  }

  function RenderizarDefensoresDoGrupo(lotacoes: IReportLotacao[]) {
    const elementos: JSX.Element[] = [];

    lotacoes.forEach((lotacao, index) => {
      const defensor = relatorio?.pessoas.find((a) => a.id == lotacao.defensor_id);

      if (defensor) {
        const defensorNome = [defensor.titulo?.trim(), defensor.nome?.trim()]
          .filter(Boolean)
          .join(' ');

        if (selectedDefensores.length > 0 && !selectedDefensores.includes(defensorNome)) {
          return;
        }

        elementos.push(
          <Fragment key={'L' + lotacao.id + index}>
            <span className={'relatorio-defensor ' + (!lotacao.ativo ? 'relatorio-defensor-inativo' : '')}>
              {defensor.titulo} {defensor.nome}
            </span>
            <br />
            <span className="fst-italic relatorio-defensor-observacao">
              {lotacao.anotacao1} {lotacao.anotacao2}
            </span>
            <br />
            {exibirEventos && (
              <span className="relatorio-defensor-folga">
                {lotacao.ativo === true && defensor?.abonos && GetEventos(defensor, lotacao.defensoria_id)}
              </span>
            )}
            <br />
            {index < lotacoes.length - 1 && <hr className="m-1" />}
          </Fragment>
        );
      }
    });

    return elementos;
  }


  function GetEventos(defensor: IReportPessoaLotada, id_defensoria: number) {
    let abonosFiltrados = defensor.abonos

    // Exibe eventos em todas as defensorias da lotação do defensor.
    if (filtroDefensoria)
      abonosFiltrados = abonosFiltrados.filter((abono) => {
        return abono.substitutos.some(
          (sub) => sub.defensoria_id === id_defensoria,
        );
      });

    return abonosFiltrados.map((folgaItem, folgaIndex) => {
      const substituicoes = folgaItem.substitutos.filter(
        (a) => a.defensoria_id === id_defensoria,
      );

      const substitutosInfo = substituicoes
        .map((substituicao) => {
          const defensorSubstituto = relatorio?.pessoas.find(
            (pessoa) => pessoa.id === substituicao.defensor_id,
          );
          if (defensorSubstituto) {
            const anotacao = substituicao.anotacao ? ` - ${substituicao.anotacao}` : '';
            return `${defensorSubstituto.nome}${anotacao}`;
          }
          return '';
        })
        .filter((info) => info !== '');

      const tipoDescanso = TipoPeriodoDescansoList.find(
        (a) => a.value === folgaItem.tipo,
      )?.name;
      const dataInicial = DataAmigavel(folgaItem.data_inicial);
      const dataFinal = DataAmigavel(folgaItem.data_final);
      const dias = DiasEntreDuasDatas(
        folgaItem.data_inicial,
        folgaItem.data_final,
      );

      return (
        <Fragment key={folgaIndex}>
          <span>
            {`(${tipoDescanso}: ${dataInicial} a ${dataFinal} (${dias} dias)`}
            {substitutosInfo.length > 0 && (
              <>
                <br />
                <strong>Substitutos:</strong> {substitutosInfo.join(', ')}
              </>
            )}
          </span>
          <br />
        </Fragment>
      );
    });
  }

  function hasSelectedDefenders(defensoria: IReportLocalLotacao): boolean {
    if (!relatorio)
      return false;

    const lotacoes = relatorio.lotacoes.filter(
      (a) => a.defensoria_id == defensoria.id && a.defensor_id,
    );

    if (selectedDefensores.length === 0) return true;

    return lotacoes.some((lotacao) => {
      const defensor = relatorio.pessoas.find(
        (a) => a.id == lotacao.defensor_id,
      );

      if (!defensor)
        return false;

      const defensorNome = [defensor.titulo?.trim(), defensor.nome?.trim()]
        .filter(Boolean)
        .join(' ');
      return selectedDefensores.includes(defensorNome);
    });
  }

  if (!relatorio) return <Carregando />;

  const extrairNumeroDefensoria = (nome: string): number | null => {
    const match = nome.match(/(\d+)[ªº]?\s/);
    return match ? parseInt(match[1], 10) : null;
  };

  const ordenarDefensorias = (defensorias: IReportLocalLotacao[]): IReportLocalLotacao[] => {
    return [...defensorias].sort((a, b) => {
      const numeroA = extrairNumeroDefensoria(a.nome);
      const numeroB = extrairNumeroDefensoria(b.nome);

      if (numeroA !== null && numeroB !== null) {
        return numeroA - numeroB;
      }

      if (numeroA !== null) return -1;
      if (numeroB !== null) return 1;

      return a.nome.localeCompare(b.nome);
    });
  };

  return (
    <Fragment>
      <div className="col-12" id="conteudo">
        <div className="text-center">
          <h4 className="text-center relatorio-font relatorio-titulo">
            LOTAÇÃO DOS DEFENSORES PÚBLICOS COM OS RESPECTIVOS ENDEREÇOS E ONDE ATUAM
          </h4>
          <span className="text-center ft-small">
            Atualizado em{' '}
            <input
              title="Data"
              type="text"
              className="form-control-plaintext"
              style={{ width: '10%', display: 'inline' }}
              defaultValue={
                relatorio
                  ? DataAmigavel(relatorio.editadoem)
                  : DataAmigavel(new Date(Date.now()))
              }
            />
          </span>
        </div>
        <br />

        {relatorio &&
          relatorio.comarcas
            .filter((comarca) => {
              const comarcaNome =
                `${comarca.prefixo || ''} ${comarca.nome}`.trim();

              if (selectedComarcas.length > 0) {
                return selectedComarcas.includes(comarcaNome);
              }

              return comarca.locais.some(
                (defensoria) =>
                  (selectedDefensorias.length === 0 ||
                    selectedDefensorias.includes(defensoria.nome)) &&
                  hasSelectedDefenders(defensoria),
              );
            })
            .map((comarca, index) => {
              titulo1++;
              let titulo2 = 0;

              const todasAsdefensorias = relatorio.comarcas.flatMap(
                (a) => a.locais,
              );
              const atuacoesDessaComarca: IReportAtuacao[] = agruparAreaAtuacao
                ? relatorio.atuacoes.filter((a) =>
                  comarca.locais.flatMap((b) => b.atuacao_id).includes(a.id),
                )
                : [{ id: 0, nome: '', ordem: 0 }];

              return (
                <Fragment key={index}>
                  <a
                    className="text-reset text-decoration-none"
                    data-bs-toggle="collapse"
                    href={'#' + TrimAll(comarca.nome)}
                    role="button"
                    aria-expanded="true"
                    aria-controls={TrimAll(comarca.nome)}
                  >
                    <h4 className="relatorio-font relatorio-comarca">
                      {titulo1}.{' '}
                      {''.concat(
                        comarca.prefixo || '',
                        comarca.prefixo ? ' ' : '',
                        comarca.nome,
                      )}
                    </h4>
                  </a>

                  {atuacoesDessaComarca.map((atuacao, index2) => {
                    titulo2++;
                    const defensoriasFiltradas = (
                      agruparAreaAtuacao
                        ? comarca.locais.filter(a => a.atuacao_id == atuacao.id)
                        : comarca.locais
                    ).filter(
                      defensoria =>
                        (selectedDefensorias.length === 0 ||
                          selectedDefensorias.includes(defensoria.nome)) &&
                        hasSelectedDefenders(defensoria),
                    );

                    const defensorias = agruparPorPredio
                      ? defensoriasFiltradas
                      : ordenarDefensorias(defensoriasFiltradas);

                    const defensoriasPorPredio: {
                      [key: number]: IReportLocalLotacao[];
                    } = {};
                    defensorias.forEach((defensoria) => {
                      const predioId = defensoria.predio_id || 0;
                      if (!defensoriasPorPredio[predioId]) {
                        defensoriasPorPredio[predioId] = [];
                      }
                      defensoriasPorPredio[predioId].push(defensoria);
                    });

                    let gruposDefensorias: Array<{ predioId: number | null; defensorias: IReportLocalLotacao[] }> = [];

                    if (agruparPorPredio) {
                      const defensoriasPorPredio: { [key: number]: IReportLocalLotacao[] } = {};
                      defensorias.forEach((defensoria) => {
                        const predioId = defensoria.predio_id || 0;
                        if (!defensoriasPorPredio[predioId]) {
                          defensoriasPorPredio[predioId] = [];
                        }
                        defensoriasPorPredio[predioId].push(defensoria);
                      });

                      // Ordenar defensorias dentro de cada prédio
                      Object.keys(defensoriasPorPredio).forEach((predioId) => {
                        defensoriasPorPredio[Number(predioId)] = ordenarDefensorias(defensoriasPorPredio[Number(predioId)]);
                      });

                      // Ordenar prédios por ID
                      gruposDefensorias = Object.entries(defensoriasPorPredio)
                        .sort(([a], [b]) => Number(a) - Number(b))
                        .map(([predioId, defensoriasDoPredio]) => ({
                          predioId: Number(predioId),
                          defensorias: defensoriasDoPredio
                        }));
                    } else {
                      gruposDefensorias = [{
                        predioId: null,
                        defensorias: defensorias
                      }];
                    }

                    return (
                      <Fragment key={index2}>
                        {defensorias.length > 0 && (
                          <Fragment>
                            {agruparAreaAtuacao && atuacao.id && (
                              <h5 className="relatorio-font relatorio-atuacao">
                                {titulo1}.{titulo2}. Das Defensorias Públicas -{' '}
                                {atuacao.nome}
                              </h5>
                            )}

                            {gruposDefensorias.map((grupo, grupoIndex) => {
                              const predioId = grupo.predioId;
                              const defensoriasDoPredio = grupo.defensorias;
                              const predioIndex = grupoIndex;
                              const predio = comarca.predios.find(
                                (p) => p.id === Number(predioId),
                              );
                              const titulo3 = predioIndex + 1;

                              return (
                                <Fragment key={`predio-${predioId}`}>
                                  {agruparPorPredio && predio && (
                                    <a
                                      className="text-reset text-decoration-none"
                                      data-bs-toggle="collapse"
                                      href={`#${TrimAll(comarca.nome)}-${predioId}`}
                                      role="button"
                                      aria-expanded="true"
                                      aria-controls={`${TrimAll(comarca.nome)}-${predioId}`}
                                    >
                                      <h6 className="relatorio-font relatorio-predio">
                                        {titulo1}.{titulo2}.{titulo3}.{' '}
                                        {predio.nome} -{' '}
                                        {predio.endereco?.logradouro} -{' '}
                                        {predio?.telefoneprincipal &&
                                          predio.telefoneprincipal}
                                      </h6>
                                    </a>
                                  )}

                                  <div
                                    id={`${TrimAll(comarca.nome)}`}
                                    className="table-responsive show"
                                    style={stylesPadding}
                                  >
                                    <table
                                      id="tabelarelatorio"
                                      className="table table-sm table-bordered align-middle text-center mt-3 relatorio-font border border-dark"
                                    >
                                      <thead>
                                        <tr>
                                          <th style={{ width: '25%' }}>
                                            ÓRGÃO DE ATUAÇÃO
                                          </th>
                                          {!agruparPorPredio && (
                                            <th style={{ width: '25%' }}>
                                              ENDEREÇO/TELEFONE
                                            </th>
                                          )}
                                          <th style={{ width: '25%' }}>
                                            DEFENSOR(A)
                                          </th>
                                          {exibirEstagiarios && (
                                            <th style={{ width: '25%' }}>
                                              ESTAGIÁRIOS(AS)
                                            </th>
                                          )}
                                          <th style={{ width: '25%' }}>
                                            SUBSTITUIÇÃO
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {defensoriasDoPredio.map(
                                          (defensoria, index3) => {
                                            const predioDefensoria =
                                              comarca.predios.find(
                                                (a) =>
                                                  a.id ==
                                                  defensoria.predio_id,
                                              );
                                            const varaDefensoria =
                                              relatorio.varas.find(
                                                (a) =>
                                                  a.id == defensoria.vara_id,
                                              );
                                            const substituta1 =
                                              todasAsdefensorias.find(
                                                (a) =>
                                                  a.id ==
                                                  defensoria.substituta1_id,
                                              );
                                            const substituta2 =
                                              todasAsdefensorias.find(
                                                (a) =>
                                                  a.id ==
                                                  defensoria.substituta2_id,
                                              );

                                            const endereco = predioDefensoria?.endereco;
                                            const enderecoStr = endereco
                                              ? [endereco.logradouro, endereco.bairro, endereco.cidade]
                                                  .filter(Boolean)
                                                  .join(' ')
                                              : '';

                                            return (
                                              <tr key={'c' + index3}>
                                                <td>
                                                  <span className="fw-bold">
                                                    {defensoria.nome ?? ''}
                                                  </span>
                                                  <br />
                                                  <br />
                                                  {varaDefensoria?.nome != null && varaDefensoria.nome !== '' && (
                                                    <span className="fst-italic">
                                                      ({varaDefensoria.nome})
                                                    </span>
                                                  )}
                                                </td>
                                                {!agruparPorPredio && (
                                                  <td>
                                                    {predioDefensoria?.nome ?? ''}
                                                    <br />
                                                    {enderecoStr && (
                                                      <>
                                                        {enderecoStr}
                                                        <br />
                                                      </>
                                                    )}
                                                    {defensoria.anotacao != null && defensoria.anotacao !== '' && (
                                                      <>
                                                        <span className="fw-bold relatorio-defensor-observacao">
                                                          {defensoria.anotacao}
                                                        </span>
                                                        <br />
                                                      </>
                                                    )}
                                                    <br />
                                                    Fórum:{' '}
                                                    {predioDefensoria?.telefoneprincipal ?? ''}
                                                    {predioDefensoria?.telefonesecundario != null &&
                                                      predioDefensoria?.telefonesecundario?.length > 1 && (
                                                        <>
                                                          <br />
                                                          {Array.isArray(predioDefensoria.telefonesecundario)
                                                            ? predioDefensoria.telefonesecundario.filter(Boolean).join(', ')
                                                            : (predioDefensoria.telefonesecundario ?? '')}
                                                        </>
                                                      )}
                                                    {defensoria.telefone != null && defensoria.telefone !== '' && (
                                                      <>
                                                        <br />
                                                        Defensoria:{' '}
                                                        {defensoria.telefone}
                                                      </>
                                                    )}
                                                  </td>
                                                )}
                                                <td>
                                                  {GetNomesDefensores(
                                                    defensoria,
                                                  )}
                                                </td>
                                                {exibirEstagiarios && (
                                                  <td>
                                                    {AgruparEstagiariosPorResponsavel(defensoria)}
                                                  </td>
                                                )}
                                                <td>
                                                  <b>1° SUBSTITUTO</b>
                                                  <br />
                                                  <span>
                                                    {substituta1?.nome ?? ''}
                                                  </span>
                                                  <br />
                                                  <br />
                                                  <b>2° SUBSTITUTO</b>
                                                  <br />
                                                  {substituta2?.nome ?? ''}
                                                </td>
                                              </tr>
                                            );
                                          },
                                        )}
                                      </tbody>
                                    </table>
                                    <br />
                                  </div>
                                </Fragment>
                              );
                            },
                            )}
                          </Fragment>
                        )}
                      </Fragment>
                    );
                  })}
                </Fragment>
              );
            })}
      </div>
    </Fragment>
  );
}